import { SafePromise } from '@benzinga/safe-await';
import { IncludeHeader, RestfulClient, Session } from '@benzinga/session';
import { SimpleNewsOptions, SimpleNewsQuery } from '@benzinga/internal-news-manager';
import {
  BreakingNewsResponse,
  ArticleSearchResponse,
  ParselyPost,
  ParselyPostParams,
  News,
  ArticleData,
  StoryObject,
  ChartbeatPost,
  ChartbeatParams,
} from '../entities';
import { BasicNewsEnvironment } from '../environment';
import { DateTime } from 'luxon';
import { removeFalsyFromQueryParams } from '@benzinga/utils';

export class BasicNewsRestful extends RestfulClient {
  constructor(session: Session, includeHeaders?: IncludeHeader) {
    super(session.getEnvironment(BasicNewsEnvironment).url, session, includeHeaders);
  }

  getArticle = (nodeId: number | string): SafePromise<ArticleData> => {
    const url = this.URL(`/api/articles/${nodeId}/camel`);
    return this.get(url, { allowsAnonymousAuth: true });
  };

  getTrendingNews = async (): SafePromise<News[][]> => {
    const url = this.URL('ajax-cache/nodequeue/4');
    return this.get(url, { allowsAnonymousAuth: true, credentials: 'same-origin' });
  };

  simplyQueryNews = async (
    query: SimpleNewsQuery = {},
    options: SimpleNewsOptions = {},
  ): SafePromise<StoryObject[]> => {
    const params = new URLSearchParams();
    const queryParams = { ...query, ...options };
    if (queryParams) {
      Object.keys(queryParams).forEach(key => {
        const value = queryParams[key];
        if (!value) return;
        params.append(key, value);
      });
    }
    const hostname = this.session.getEnvironment(BasicNewsEnvironment).simplyQueryUrl;
    const url = this.URL(`${hostname}api/news?${params}`);
    return this.get(url, { allowsAnonymousAuth: true, credentials: 'same-origin', mode: 'no-cors' });
  };

  getBreakingNews = (): SafePromise<BreakingNewsResponse> => {
    const url = this.URL(`ajax-cache/breakingevents`);
    return this.get(url, {
      allowsAnonymousAuth: true,
      cache: 'no-store',
      credentials: 'same-origin',
    });
  };

  getLatestArticles = (query: string): SafePromise<ArticleSearchResponse> => {
    const url = this.URL(`ajax-cache/marketfy/latest/${query}`);
    return this.get(url, {
      allowsAnonymousAuth: true,
      credentials: 'same-origin',
      mode: 'cors',
    });
  };

  getTopStoryIdsFromNodeQueue = (): SafePromise<number[]> => {
    const url = this.URL(`ajax-cache/nodequeue/4`);
    return this.get(url, {
      allowsAnonymousAuth: true,
      credentials: 'same-origin',
    });
  };

  getTopPostsUsingChartbeat = async (params: ChartbeatParams = {}): SafePromise<{ pages: ChartbeatPost[] }> => {
    const queryParams = {
      host: 'benzinga.com',
      limit: '10',
      types: '1',
      ...params,
    } as { [key: string]: string };

    const cleanedParams = removeFalsyFromQueryParams(new URLSearchParams(queryParams).toString());
    const hostname = this.session.getEnvironment(BasicNewsEnvironment).chartBeatUrl;
    const url = this.URL(`${hostname}api/chartbeat?${cleanedParams}`);
    return this.get(url, {
      allowsAnonymousAuth: true,
      credentials: 'same-origin',
    });
  };
}
